import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { ChangeEvent, useState } from 'react';
import { Field, Form, InjectedFormProps, reduxForm } from 'redux-form';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import CheckboxField from 'dashboard/components/FormControls/CheckboxField';
import InputField from 'dashboard/components/FormControls/InputField';
import Banner from 'dashboard/components-lib/Banner';
import { Environment } from 'dashboard/models/environment';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import { ApiModel } from 'lib/interfaces/api';
import { isRequired, matchesRegexp, withMessage } from 'lib/utils/formValidation';

const validate = {
  emailAddress: [
    withMessage('Please enter an email address', isRequired),
    withMessage('The email you entered is not valid', matchesRegexp(EMAIL_ADDRESS_REGEX)),
  ],
};

const Header = styled('h4')`
  margin-bottom: ${SPACINGS.base * 2}px;
`;

const Container = styled('div')`
  margin-top: ${SPACINGS.base}px;
`;

const StyledButton = styled(Button)`
  margin-top: ${SPACINGS.base * 3}px;
  width: 50%;
`;

const StyledBanner = styled(Banner)`
  padding: 0px ${Spacing[4]};
`;

interface FormResumeInquiryProps {
  currentEnvironment: Environment;
}

const FormResumeInquiry = ({
  handleSubmit,
  currentEnvironment,
}: FormResumeInquiryProps & InjectedFormProps<ResumeInquiryFormData, FormResumeInquiryProps>) => {
  const [emailIsChecked, setEmailIsChecked] = useState<boolean>(false);

  const toggleEmailField = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setEmailIsChecked(checked);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Header>
        Create a unique link for individuals to resume their incomplete inquiry (e.g. pending or
        expired) to complete the verification process.
      </Header>
      <Field
        component={CheckboxField}
        name='sendEmailAddress'
        id='resume-inquiry-modal__allow-send-email'
        label='Send email to recipient'
        data-test='resume-inquiry-modal__allow-send-email'
        onChange={toggleEmailField}
      />
      {emailIsChecked && (
        <Container>
          {currentEnvironment.type === ApiModel.EnvironmentSandbox && (
            <>
              <StyledBanner icon={<CircleInfoIcon />}>
                Sandbox is for testing and demo purposes. On production you can email inquiry links
                to anyone!
              </StyledBanner>
              <Spacer height={2} />
            </>
          )}
          <Field
            name='emailAddress'
            component={InputField}
            placeholder='Email address'
            validate={validate.emailAddress}
            data-test='resume-inquiry-modal__email-address'
            disabled={currentEnvironment.type === ApiModel.EnvironmentSandbox}
          />
        </Container>
      )}
      <StyledButton
        type='submit'
        value='Submit'
        name='resume-inquiry.resume'
        data-test='resume-inquiry-modal__submit'
      >
        Create link
      </StyledButton>
    </Form>
  );
};

export const formName = 'resumeInquiry';

export interface ResumeInquiryFormData {
  emailAddress?: string;
  sendEmailAddress?: boolean;
}

export default reduxForm<ResumeInquiryFormData, FormResumeInquiryProps>({
  form: formName,
})(FormResumeInquiry);
